import * as React from 'react'

import type { ICalendarEvent } from '@/features/calendar/interfaces'
import { areIntervalsOverlapping, format, getYear, isWithinInterval, parseISO } from 'date-fns'

import { DoorClosed } from 'lucide-react'
import { ScrollArea } from '@/components/ui/scroll-area'

import { CreateEventDialog } from '@/features/calendar/components/dialogs/add-event-dialog'
import { CalendarTimeline } from '@/features/calendar/components/week-and-day-view/calendar-time-line'
import { DayViewMultiDayEventsRow } from '@/features/calendar/components/week-and-day-view/day-view-multi-day-events-row'
import { EventBlock } from '@/features/calendar/components/week-and-day-view/event-block'

import { getTrainingBlockStyle, groupTrainings } from '@/features/calendar/helpers'

import { useCalendar } from '../../contexts/use-calendar'
import { Calendar as CalendarPicker } from '@/components/ui/calendar'
import { Separator } from '@/components/ui/separator'

interface IProps {
  singleDayTrainings: ICalendarEvent[]
  multiDayTrainings: ICalendarEvent[]
}

export function CalendarDayView({ multiDayTrainings, singleDayTrainings }: IProps) {
  const { selectedDate, setSelectedDate, trainings, canManageEvents } = useCalendar()
  const [visibleMonth, setVisibleMonth] = React.useState(() => new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1))

  React.useEffect(() => {
    setVisibleMonth(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1))
  }, [selectedDate])

  const handleMonthChange = React.useCallback((month: Date) => {
    setVisibleMonth(month)

    const desiredDay = selectedDate.getDate()
    const daysInTargetMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate()
    const nextSelectedDate = new Date(month)

    nextSelectedDate.setDate(Math.min(desiredDay, daysInTargetMonth))
    nextSelectedDate.setHours(
      selectedDate.getHours(),
      selectedDate.getMinutes(),
      selectedDate.getSeconds(),
      selectedDate.getMilliseconds(),
    )

    setSelectedDate(nextSelectedDate)
  }, [selectedDate, setSelectedDate])

  const hours = Array.from({ length: 24 }, (_, i) => i)

  const getCurrentTrainings = (trainings: ICalendarEvent[]) => {
    const now = new Date()

    return (
      trainings.filter(training =>
        isWithinInterval(now, {
          start: parseISO(training.startAt),
          end: parseISO(training.endAt),
        }),
      ) || null
    )
  }

  const currentTrainings = getCurrentTrainings([...singleDayTrainings, ...multiDayTrainings])

  const dayTrainings = singleDayTrainings.filter((training) => {
    const trainingDate = parseISO(training.startAt)
    return (
      trainingDate.getDate() === selectedDate.getDate()
      && trainingDate.getMonth() === selectedDate.getMonth()
      && trainingDate.getFullYear() === selectedDate.getFullYear()
    )
  })

  const groupedTrainings = groupTrainings(dayTrainings)

  return (
    <div className="flex">
      <div className="flex flex-1 flex-col">
        <div>
          <DayViewMultiDayEventsRow selectedDate={selectedDate} multiDayTrainings={multiDayTrainings} />

          {/* Day header */}
          <div className="relative z-20 flex border-b">
            <div className="w-18"></div>
            <span className="flex-1 border-l py-2 text-center text-xs font-medium text-t-quaternary">
              {format(selectedDate, 'EE')}
              {' '}
              <span className="font-semibold text-t-secondary">{format(selectedDate, 'd')}</span>
            </span>
          </div>
        </div>

        <ScrollArea className="h-[800px]" type="always">
          <div className="flex">
            {/* Hours column */}
            <div className="relative w-18">
              {hours.map((hour, index) => (
                <div key={hour} className="relative" style={{ height: '96px' }}>
                  <div className="absolute -top-3 right-2 flex h-6 items-center">
                    {index !== 0 && <span className="text-xs text-t-quaternary">{format(new Date().setHours(hour), 'hh a')}</span>}
                  </div>
                </div>
              ))}
            </div>

            {/* Day grid */}
            <div className="relative flex-1 border-l">
              <div className="relative">
                {hours.map((hour, index) => (
                  <div key={hour} className="relative" style={{ height: '96px' }}>
                    {index !== 0 && <div className="pointer-events-none absolute inset-x-0 top-0 border-b"></div>}

                    {canManageEvents ? (
                      <CreateEventDialog startDate={selectedDate} startTime={{ hour, minute: 0 }}>
                        <div className="absolute inset-x-0 top-0 h-[48px] cursor-pointer transition-colors hover:bg-bg-primary-hover" />
                      </CreateEventDialog>
                    ) : (
                      <div className="absolute inset-x-0 top-0 h-[48px] pointer-events-none" />
                    )}

                    <div className="pointer-events-none absolute inset-x-0 top-1/2 border-b border-dashed border-b-tertiary"></div>

                    {canManageEvents ? (
                      <CreateEventDialog startDate={selectedDate} startTime={{ hour, minute: 30 }}>
                        <div className="absolute inset-x-0 top-[48px] h-[48px] cursor-pointer transition-colors hover:bg-bg-primary-hover" />
                      </CreateEventDialog>
                    ) : (
                      <div className="absolute inset-x-0 top-[48px] h-[48px] pointer-events-none" />
                    )}
                  </div>
                ))}

                {
                  groupedTrainings.map((group, groupIndex) => group.map((training) => {
                    let style = getTrainingBlockStyle(training, selectedDate, groupIndex, groupedTrainings.length)
                    const hasOverlap = groupedTrainings.some(
                      (otherGroup, otherIndex) =>
                        otherIndex !== groupIndex
                        && otherGroup.some(otherTraining =>
                          areIntervalsOverlapping(
                            { start: parseISO(training.startAt), end: parseISO(training.startAt) },
                            { start: parseISO(otherTraining.startAt), end: parseISO(otherTraining.endAt) },
                          ),
                        ),
                    )

                    if (!hasOverlap)
                      style = { ...style, width: '100%', left: '0%' }

                    return (
                      <div key={training.id} className="absolute p-1" style={style}>
                        <EventBlock training={training} />
                      </div>
                    )
                  }))
                }
              </div>

              <CalendarTimeline />
            </div>
          </div>
        </ScrollArea>
      </div>

      <div className="hidden w-80 flex-col border-l bg-muted/10 md:flex">
        <div className="p-4">
          <CalendarPicker
            mode="single"
            selected={selectedDate}
            onSelect={setSelectedDate}
            month={visibleMonth}
            onMonthChange={handleMonthChange}
            captionLayout="dropdown"
            startMonth={new Date(2025, 0)}
            endMonth={new Date(getYear(new Date().setFullYear(new Date().getFullYear() + 10)), 11)}
            className="rounded-xl border border-border bg-card shadow-sm"
            onDayClick={(day, modifiers) => {
              if (modifiers.disabled)
                return
              setSelectedDate(day)
            }}
          />
        </div>

        <Separator />

        <div className="flex-1 space-y-3 overflow-hidden">
          {currentTrainings.length > 0
            ? (
              <div className="flex items-start gap-2 px-4 pt-4">
                <span className="relative mt-[5px] flex size-2.5">
                  <span className="absolute inline-flex size-full animate-ping rounded-full bg-green-500 opacity-75"></span>
                  <span className="relative inline-flex size-2.5 rounded-full bg-green-500"></span>
                </span>

                <p className="text-sm font-semibold text-t-secondary">Event Started</p>
              </div>
            )
            : (
              <p className="p-6 text-center text-sm italic text-t-tertiary">No events scheduled</p>
            )}

          {currentTrainings.length > 0 && (
            <ScrollArea className="h-[420px] px-4" type="always">
              <div className="space-y-4 pb-4">
                {currentTrainings.map((t) => {
                  const training = trainings.find(training => training.id === t.id)

                  return (
                    <div key={training?.id ?? Math.random()} className="rounded-md border p-3 hover:bg-muted/50 transition-colors">
                      <p className="line-clamp-2 text-sm font-semibold mb-2">{training?.location}</p>

                      {
                        training && (
                          <div className="flex items-center gap-1.5">
                            <DoorClosed className="size-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">{training.title}</span>
                          </div>
                        )
                      }

                      {training?.startAt && training?.endAt && (
                        <div className="text-xs text-muted-foreground mt-2">
                          {format(parseISO(training.startAt), 'h:mm a')} - {format(parseISO(training.endAt), 'h:mm a')}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </ScrollArea>
          )}
        </div>
      </div>
    </div>
  )
}
